/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import {
  useCommitItemsImportMutation,
  usePreviewItemsImportMutation,
} from '@/hooks/useItems'
import type {
  ItemImportMode,
  ItemImportOptions,
  ItemImportPreview,
} from '@/types/backend'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

type ColumnsState = {
  name: string
  sku: string
  unitPrice: string
  category: string
  packaging: string
}

export function ImportItemsDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const previewMut = usePreviewItemsImportMutation()
  const commitMut = useCommitItemsImportMutation()

  const [file, setFile] = useState<File | null>(null)

  // options
  const [delimiter, setDelimiter] = useState<string>(',')
  const [mode, setMode] = useState<ItemImportMode>('UPSERT_BY_SKU')
  const [createCategories, setCreateCategories] = useState<boolean>(true)

  // mapping (optionnel)
  const [columns, setColumns] = useState<ColumnsState>({
    name: 'name',
    sku: 'sku',
    unitPrice: 'unitPrice',
    category: 'category',
    packaging: 'packaging',
  })

  const options = useMemo<ItemImportOptions>(() => {
    const oneCharDelimiter = (delimiter ?? ',').slice(0, 1) || ','

    return {
      delimiter: oneCharDelimiter,
      mode,
      createCategories,
      columns: {
        name: columns.name?.trim() || undefined,
        sku: columns.sku?.trim() || undefined,
        unitPrice: columns.unitPrice?.trim() || undefined,
        category: columns.category?.trim() || undefined,
        packaging: columns.packaging?.trim() || undefined,
      },
    }
  }, [delimiter, mode, createCategories, columns])

  const preview = previewMut.data as ItemImportPreview | undefined

  // --- reset preview quand input change (évite preview "stale")
  useEffect(() => {
    if (!open) return
    previewMut.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, delimiter, mode, createCategories, columns, open])

  const canPreview = !!file && !previewMut.isPending && !commitMut.isPending
  const canCommit =
    !!file &&
    !!preview &&
    (preview.summary?.invalid ?? 0) === 0 &&
    !commitMut.isPending

  const resetAll = () => {
    setFile(null)
    setDelimiter(',')
    setMode('UPSERT_BY_SKU')
    setCreateCategories(true)
    setColumns({
      name: 'name',
      sku: 'sku',
      unitPrice: 'unitPrice',
      category: 'category',
      packaging: 'packaging',
    })
    previewMut.reset()
    commitMut.reset()
  }

  const close = () => {
    onOpenChange(false)
    resetAll()
  }

  const onFileChange = (f: File | null) => {
    setFile(f)
  }

  const doPreview = async () => {
    if (!file) return
    try {
      const res = await previewMut.mutateAsync({ file, options })
      toast.success('Preview OK', {
        description: `${res.summary.total} lignes — ${res.summary.valid} OK — ${res.summary.invalid} erreurs — create ${res.summary.willCreate} / update ${res.summary.willUpdate}`,
      })
    } catch (e: any) {
      toast.error('Preview échoué', {
        description:
          e?.response?.data?.message ?? e?.message ?? 'Erreur inconnue',
      })
    }
  }

  const doCommit = async () => {
    if (!file) return
    if (!preview) {
      toast.error('Faites un preview avant d’importer.')
      return
    }
    if ((preview.summary?.invalid ?? 0) > 0) {
      toast.error('Corrigez les erreurs du CSV puis refaites Preview.')
      return
    }

    try {
      const res = await commitMut.mutateAsync({ file, options })
      toast.success('Import terminé', {
        description: `${res.total} traités — ${res.created} créés — ${res.updated} mis à jour`,
      })
      close()
    } catch (e: any) {
      toast.error('Import échoué', {
        description:
          e?.response?.data?.message ?? e?.message ?? 'Erreur inconnue',
      })
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        if (!v) resetAll()
      }}
      title='Importer produits (CSV)'
      description='Faites un preview, corrigez, puis importez.'
      footer={
        <div className='flex items-center gap-2'>
          <Button
            type='button'
            variant='secondary'
            disabled={!canPreview}
            onClick={doPreview}
          >
            Preview
          </Button>
          <ModalActions
            onConfirm={doCommit}
            confirmLabel='Importer'
            confirmLoading={commitMut.isPending}
            confirmDisabled={!canCommit}
          />
        </div>
      }
    >
      <div className='px-4 py-2 space-y-4'>
        {/* Fichier */}
        <div className='grid gap-1.5'>
          <Label>Fichier CSV</Label>
          <Input
            type='file'
            accept='.csv,text/csv'
            onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <div className='text-xs text-muted-foreground'>
              Sélectionné : <b>{file.name}</b>
            </div>
          ) : null}
        </div>

        {/* ✅ Info claire: ce que fait l'import */}
        <Alert>
          <AlertTitle>Ce que fait cet import</AlertTitle>
          <AlertDescription className='space-y-2'>
            <div>
              Cet import sert à créer / mettre à jour des <b>produits</b> (nom,
              prix, SKU, catégorie).
            </div>
            <div>
              Pour importer des <b>quantités de stock</b>, utilisez{' '}
              <b>Stock initial</b>.
            </div>

            <div className='text-xs text-muted-foreground'>
              <b>Requis</b> : <code>name</code>, <code>unitPrice</code> (&gt; 0)
              <br />
              <b>Optionnel</b> : <code>sku</code>, <code>category</code>,{' '}
              <code>packaging</code>
            </div>

            <div className='text-xs text-muted-foreground'>
              <b>Modes</b> :
              <br />
              <b>Create only</b> refuse les SKU déjà existants.
              <br />
              <b>Upsert SKU</b> met à jour si le SKU existe, sinon crée.
            </div>
          </AlertDescription>
        </Alert>

        {/* Délimiteur */}
        <div className='rounded-md border p-3 space-y-2'>
          <div className='text-sm font-medium'>Délimiteur</div>
          <div className='flex flex-wrap items-center gap-2'>
            <Button
              type='button'
              variant={delimiter === ',' ? 'default' : 'secondary'}
              onClick={() => setDelimiter(',')}
            >
              ,
            </Button>
            <Button
              type='button'
              variant={delimiter === ';' ? 'default' : 'secondary'}
              onClick={() => setDelimiter(';')}
            >
              ;
            </Button>
            <Button
              type='button'
              variant={delimiter === '\t' ? 'default' : 'secondary'}
              onClick={() => setDelimiter('\t')}
            >
              TAB
            </Button>
            <div className='flex items-center gap-2'>
              <span className='text-xs text-muted-foreground'>Custom</span>
              <Input
                className='w-[80px]'
                value={delimiter === '\t' ? '\\t' : delimiter}
                onChange={(e) => {
                  const v = e.target.value
                  if (!v) return setDelimiter(',')
                  if (v === '\\t') return setDelimiter('\t')
                  setDelimiter(v.slice(0, 1))
                }}
              />
            </div>
          </div>
          <div className='text-xs text-muted-foreground'>
            Utilise un seul caractère (ex: <b>,</b> ou <b>;</b>).
          </div>
        </div>

        {/* Mode */}
        <div className='rounded-md border p-3 space-y-2'>
          <div className='text-sm font-medium'>Mode</div>
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              variant={mode === 'CREATE_ONLY' ? 'default' : 'secondary'}
              onClick={() => setMode('CREATE_ONLY')}
            >
              Create only
            </Button>
            <Button
              type='button'
              variant={mode === 'UPSERT_BY_SKU' ? 'default' : 'secondary'}
              onClick={() => setMode('UPSERT_BY_SKU')}
            >
              Upsert SKU
            </Button>
          </div>
          <div className='text-xs text-muted-foreground'>
            <b>Create only</b> refuse les doublons. <b>Upsert SKU</b> met à jour
            si SKU existe.
          </div>
        </div>

        {/* Créer catégories */}
        <div className='flex items-center justify-between rounded-md border p-3'>
          <div className='space-y-0.5'>
            <div className='text-sm font-medium'>
              Créer catégories manquantes
            </div>
            <div className='text-xs text-muted-foreground'>
              Si une catégorie n’existe pas, on la crée pendant l’import.
            </div>
          </div>
          <Switch
            checked={createCategories}
            onCheckedChange={setCreateCategories}
          />
        </div>

        {/* Mapping */}
        <div className='rounded-md border p-3 space-y-3'>
          <div className='text-sm font-medium'>Colonnes (optionnel)</div>
          <div className='text-xs text-muted-foreground'>
            Mets vide pour ignorer une colonne. <b>name</b> et <b>unitPrice</b>{' '}
            doivent exister côté CSV.
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <Field label='name'>
              <Input
                value={columns.name}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, name: e.target.value }))
                }
              />
            </Field>
            <Field label='sku'>
              <Input
                value={columns.sku}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, sku: e.target.value }))
                }
              />
            </Field>
            <Field label='unitPrice'>
              <Input
                value={columns.unitPrice}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, unitPrice: e.target.value }))
                }
              />
            </Field>
            <Field label='category'>
              <Input
                value={columns.category}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, category: e.target.value }))
                }
              />
            </Field>
            <Field label='packaging'>
              <Input
                value={columns.packaging}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, packaging: e.target.value }))
                }
              />
            </Field>
          </div>
        </div>

        {/* Preview */}
        {preview ? (
          <div className='rounded-md border p-3 text-sm space-y-2'>
            <div className='font-medium'>Résumé preview</div>
            <div className='text-muted-foreground'>
              Total: <b>{preview.summary.total}</b> — OK:{' '}
              <b>{preview.summary.valid}</b> — Erreurs:{' '}
              <b>{preview.summary.invalid}</b>
              <br />
              Create: <b>{preview.summary.willCreate}</b> — Update:{' '}
              <b>{preview.summary.willUpdate}</b>
            </div>

            {(preview.errors?.length ?? 0) > 0 ? (
              <div className='mt-2'>
                <div className='font-medium text-red-600'>
                  Erreurs (extrait)
                </div>
                <ul className='list-disc pl-5 text-xs'>
                  {preview.errors.slice(0, 10).map((e, idx) => (
                    <li key={idx}>
                      L{e.index}: {e.message}
                    </li>
                  ))}
                </ul>
                <div className='mt-2 text-xs text-muted-foreground'>
                  Corrige le CSV puis relance <b>Preview</b>.
                </div>
              </div>
            ) : (
              <div className='text-xs text-green-700'>
                Aucune erreur bloquante.
              </div>
            )}
          </div>
        ) : null}
      </div>
    </AppDialog>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className='grid gap-1.5'>
      <Label>{label}</Label>
      {children}
    </div>
  )
}
